import { useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { Download, FileUp, Save, SlidersHorizontal } from 'lucide-react'
import {
  createSyntheticCalibrationDataset,
  exportCalibrationCsv,
  fitCalibration,
  parseCalibrationCsv,
} from '../calibration'
import { downloadText } from '../io'
import type {
  AppState,
  CalibrationDataset,
  CalibrationFit,
  SimulationResult,
} from '../types'
import { HorizontalScrollRegion } from './HorizontalScrollRegion'
import { MeasurementPlot } from './Plots'
import { EmptyState, Metric, ModelBadge, Notice, Section, formatNumber } from './ui'

interface CalibrationTabProps {
  state: AppState
  setState: Dispatch<SetStateAction<AppState>>
  dataset: CalibrationDataset | null
  setDataset: (dataset: CalibrationDataset | null) => void
  fit: CalibrationFit | null
  fitStale: boolean
  onFit: (fit: CalibrationFit) => void
  simulationResult: SimulationResult | null
  simulationStale: boolean
}

export function CalibrationTab({
  state,
  setState,
  dataset,
  setDataset,
  fit,
  fitStale,
  onFit,
  simulationResult,
  simulationStale,
}: CalibrationTabProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [errors, setErrors] = useState<string[]>([])

  const importFile = async (file: File) => {
    const result = parseCalibrationCsv(await file.text(), file.name)
    if (!result.dataset) {
      setErrors(result.errors)
      return
    }
    setErrors([])
    setDataset(result.dataset)
  }

  const applyPhase = () => {
    if (!fit || !Number.isFinite(fit.fittedLensPhaseMm)) return
    setState((current) => ({
      ...current,
      lens:
        current.lens.mode === 'thin'
          ? {
              ...current.lens,
              thin: { ...current.lens.thin, lateralOffsetMm: fit.fittedLensPhaseMm },
            }
          : {
              ...current.lens,
              thick: { ...current.lens.thick, lateralOffsetMm: fit.fittedLensPhaseMm },
            },
    }))
  }

  return (
    <div className="tab-stack">
      <div className="result-header">
        <div>
          <ModelBadge>measured or calibration-fitted result</ModelBadge>
          <h1>Calibration</h1>
          <p>Import measured view-zone intensity, inspect it separately, and fit a first-order zone-center model without overwriting design inputs.</p>
        </div>
        <div className="toolbar">
          <button className="secondary-button" onClick={() => inputRef.current?.click()}><FileUp size={15} /> Import CSV</button>
          <button className="secondary-button" onClick={() => { setErrors([]); setDataset(createSyntheticCalibrationDataset(state)) }}>Load synthetic example</button>
          <input ref={inputRef} type="file" aria-label="Import calibration CSV" accept=".csv,text/csv" className="sr-only" onChange={(event) => {
            const file = event.target.files?.[0]
            if (file) void importFile(file)
            event.target.value = ''
          }} />
        </div>
      </div>

      <Notice>
        Expected columns: <code>x_mm,view_0,view_1,…</code>. Positions must be strictly increasing; values must be finite and nonnegative. Imported samples remain unchanged in memory and exports.
      </Notice>
      {errors.length ? <Notice tone="danger" title="Measurement import rejected">{errors.join(' ')}</Notice> : null}

      {!dataset ? (
        <EmptyState title="No measurement loaded">Import a CSV or load the labeled synthetic example. At least three positions and one view curve are required.</EmptyState>
      ) : (
        <>
          <Section
            title={dataset.name}
            description={`${dataset.xMm.length} positions, ${dataset.viewNames.length} view curves. Solid curves are measured.`}
            actions={
              <div className="toolbar">
                <button className="secondary-button" onClick={() => downloadText('lenticular-calibration.csv', exportCalibrationCsv(dataset), 'text/csv')}><Download size={15} /> Export measurements</button>
                <button className="danger-button" onClick={() => setDataset(null)}>Remove data</button>
              </div>
            }
          >
            {simulationStale ? <Notice tone="warning">The simulated overlay is stale and has been hidden until the ray model is re-run.</Notice> : null}
            <MeasurementPlot dataset={dataset} simulated={!simulationStale ? simulationResult?.intensity : null} />
          </Section>

          <Section title="Calibration fit" description="The v2 fit regresses validated primary-lobe centroids against the known source offsets. Source phase and all optical inputs remain held fixed.">
            <div className="action-row">
              <button className="primary-button" onClick={() => onFit(fitCalibration(dataset, state))}><SlidersHorizontal size={15} /> Fit zone centers</button>
              {fit ? <button className="secondary-button" onClick={() => downloadText('lenticular-calibration-fit.json', JSON.stringify({ schema: 'lenticular-display-lab/calibration-fit', version: 2, units: 'mm', measurement: dataset.name, estimator: fit.estimator, fit }, null, 2), 'application/json')}><Download size={15} /> Export fit</button> : null}
            </div>
            {!fit ? (
              <p className="result-note">No fit has been run for this measurement and setup.</p>
            ) : (
              <>
                {fitStale ? <Notice tone="warning" title="Fit is stale">An optical or view-design input changed after fitting. The fit remains visible but does not describe the current setup.</Notice> : null}
                <div className="section-model-row compact-top"><ModelBadge>{fit.model}</ModelBadge><span>{fit.estimator}; affine ordinary least squares; no intensity gain fitted</span></div>
                <div className="metric-grid compact-top">
                  <Metric label="Fitted reduced gap" value={formatNumber(fit.fittedReducedGapMm, 6)} unit="mm" />
                  <Metric label="Fitted adjacent spacing" value={formatNumber(fit.fittedZoneSpacingMm, 4)} unit="mm" />
                  <Metric label="Fitted lens phase" value={formatNumber(fit.fittedLensPhaseMm, 6)} unit="mm" />
                  <Metric label="Center slope" value={formatNumber(fit.centerFitSlope, 5)} unit="mm/mm source" />
                  <Metric label="Center intercept" value={formatNumber(fit.centerFitIntercept, 4)} unit="mm" />
                  <Metric label="Affine consistency RMS" value={formatNumber(fit.rmsCenterResidualMm, 4)} unit="mm" />
                </div>
                <HorizontalScrollRegion className="table-scroll compact-top" label="Measured calibration results">
                  <table className="data-table"><thead><tr><th>View</th><th>Measured centroid</th><th>Measured FWHM</th><th>Measured crosstalk at target</th></tr></thead><tbody>{fit.measuredCentersMm.map((center, index) => <tr key={index}><td>{dataset.viewNames[index] ?? `view_${index}`}</td><td>{formatNumber(center, 4)} mm</td><td>{formatNumber(fit.measuredFwhmMm[index], 4)} mm</td><td>{formatNumber((fit.measuredCrosstalk[index] ?? Number.NaN) * 100, 2)}%</td></tr>)}</tbody></table>
                </HorizontalScrollRegion>
                {fit.warnings.map((warning) => <Notice key={warning} tone="warning">{warning}</Notice>)}
                <div className="action-row">
                  <button className="secondary-button" onClick={applyPhase} disabled={fitStale || !Number.isFinite(fit.fittedLensPhaseMm)}><Save size={15} /> Apply fitted lens phase</button>
                  <span className="action-note">Application is explicit and changes only the active lens lateral phase. Reduced gap and other fitted values are not written automatically.</span>
                </div>
              </>
            )}
          </Section>
        </>
      )}
    </div>
  )
}
